import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ProtectedRoute } from '../../components/auth/ProtectedRoute';
import { TenantRole, useTenantRoles } from '../../hooks/useTenants';
import { useUser, User } from '../../hooks/useUsers';
import { apiClient } from '../../services/api';
import toast from 'react-hot-toast';
import {
  UserIcon,
  ShieldCheckIcon,
  KeyIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  CalendarIcon,
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';

const UserDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser, currentTenant, hasPermission } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  // Get the user using the hook
  const { user, loading: userLoading } = useUser(id || '');

  // Roles are sourced from the dedicated TenantRole endpoint, not embedded
  // on the canonical Tenant.
  const { roles: tenantRoles } = useTenantRoles(currentTenant?.id || '');

  const userTenant = useMemo(() => {
    if (!user || !currentTenant) return null;
    return (user.tenants ?? []).find((t: any) => t.tenantId === currentTenant.id) as any;
  }, [user, currentTenant]);

  const userRole = useMemo(() => {
    if (!userTenant || !currentTenant) return null;
    return tenantRoles.find((r) => r.name === userTenant.role);
  }, [userTenant, currentTenant, tenantRoles]);

  useEffect(() => {
    if (userTenant) {
      setSelectedRole(userTenant.role);
    }
    if (userRole) {
      setSelectedPermissions(userRole.permissions);
    }
  }, [userTenant, userRole]);

  const canManageUsers = hasPermission('users.write') || hasPermission('tenant.users.manage');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'inactive':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50';
      case 'inactive':
        return 'text-red-600 bg-red-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const handleSave = async () => {
    if (!user || !currentTenant) {
      toast.error('User or tenant not available');
      return;
    }

    try {
      // Find the role ID from the selected role name
      const role = tenantRoles.find((r) => r.name === selectedRole);
      if (!role) {
        toast.error('Selected role not found');
        return;
      }

      // Update user role in tenant
      const response = await apiClient.updateUserRole(user.id, currentTenant.id, role.id);

      if (response.success) {
        toast.success('User role updated successfully');
        setIsEditing(false);
        // Refresh to show updated data
        window.location.reload();
      } else {
        toast.error(response.error || 'Failed to update user role');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!user) {
      toast.error('User not available');
      return;
    }

    try {
      const response = await apiClient.deleteUser(user.id);

      if (response.success) {
        toast.success('User deleted successfully');
        navigate('/tenant/users');
      } else {
        toast.error(response.error || 'Failed to delete user');
        setShowDeleteConfirm(false);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
      setShowDeleteConfirm(false);
    }
  };

  const togglePermission = (permission: string) => {
    setSelectedPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  if (!user || !userTenant) {
    return (
      <ProtectedRoute requiredPermission="tenant.users.view">
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <UserIcon className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-2 text-sm font-medium text-foreground">User not found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              The user you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/tenant/users')}
                className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Users
              </button>
            </div>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  // Database.User.security is an intentionally-opaque metadata map; narrow it
  // to the console-local shape at the read site rather than enumerating
  // speculative sub-structure into the canonical schema.
  const userSecurity = user.security as
    | {
        mfaEnabled?: boolean;
        lastPasswordChange?: string;
        sessions?: Array<{
          deviceType?: string;
          location?: string;
          lastActiveAt?: string;
        }>;
      }
    | undefined;

  return (
    <ProtectedRoute requiredPermission="tenant.users.view">
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => navigate('/tenant/users')}
                  className="mr-4 p-2 rounded-lg hover:bg-accent transition-colors"
                >
                  <ArrowLeftIcon className="h-5 w-5 text-muted-foreground" />
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{user.name}</h1>
                  <p className="mt-1 text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>
              {canManageUsers && (
                <div className="flex items-center space-x-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleSave}
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <CheckIcon className="h-5 w-5 mr-2" />
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setSelectedRole(userTenant.role);
                          setSelectedPermissions(userRole?.permissions || []);
                        }}
                        className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5 mr-2" />
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        <PencilIcon className="h-5 w-5 mr-2" />
                        Edit
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <TrashIcon className="h-5 w-5 mr-2" />
                        Remove
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">User Information</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 text-muted-foreground mr-3" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Status</p>
                        <div className="flex items-center mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status ?? 'active')}`}>
                            {getStatusIcon(user.status ?? 'active')}
                            <span className="ml-1 capitalize">{user.status ?? 'active'}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ShieldCheckIcon className="h-5 w-5 text-muted-foreground mr-3" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Role</p>
                        {isEditing ? (
                          <select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            className="mt-1 px-3 py-1 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            {tenantRoles.map((role) => (
                              <option key={role.name} value={role.name}>{role.name}</option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-sm text-foreground mt-1">{userRole?.name || 'Unknown'}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-muted-foreground mr-3" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Joined</p>
                        <p className="text-sm text-foreground mt-1">
                          {userTenant.joinedAt ? new Date(userTenant.joinedAt).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-muted-foreground mr-3" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Last Active</p>
                        <p className="text-sm text-foreground mt-1">
                          {user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleString() : 'Never'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Permissions</h2>
                {isEditing ? (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground mb-4">
                      Select permissions for this user. These will override role-based permissions.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {Object.entries({
                        'User Management': ['users.read', 'users.write', 'users.delete'],
                        'Tenant Management': ['tenant.read', 'tenant.update', 'tenant.users.manage'],
                        'Network Operations': ['network.monitor', 'network.configure', 'network.manage'],
                        'Security': ['security.view', 'security.configure', 'security.audit'],
                      }).map(([category, perms]) => (
                        <div key={category} className="space-y-2">
                          <h3 className="text-sm font-medium text-foreground">{category}</h3>
                          {perms.map(perm => (
                            <label key={perm} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(perm)}
                                onChange={() => togglePermission(perm)}
                                className="rounded border-border text-primary focus:ring-primary"
                              />
                              <span className="text-sm text-muted-foreground">{perm}</span>
                            </label>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {userRole?.permissions.length ? (
                      <div className="flex flex-wrap gap-2">
                        {userRole.permissions.map(permission => (
                          <span
                            key={permission}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                          >
                            {permission}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No permissions assigned</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Security & Activity */}
            <div className="space-y-6">
              {/* Security Settings */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Security</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <KeyIcon className="h-5 w-5 text-muted-foreground mr-3" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Multi-Factor Auth</p>
                        <p className="text-xs text-muted-foreground">
                          {userSecurity?.mfaEnabled ? 'Enabled' : 'Disabled'}
                        </p>
                      </div>
                    </div>
                    {userSecurity?.mfaEnabled ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-red-500" />
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-medium text-foreground mb-2">Recent Sessions</p>
                    <div className="space-y-2">
                      {userSecurity?.sessions?.slice(0, 3).map((session, index) => (
                        <div key={index} className="flex items-center text-xs">
                          {session.deviceType === 'mobile' ? (
                            <DevicePhoneMobileIcon className="h-4 w-4 text-muted-foreground mr-2" />
                          ) : session.deviceType === 'desktop' ? (
                            <ComputerDesktopIcon className="h-4 w-4 text-muted-foreground mr-2" />
                          ) : (
                            <GlobeAltIcon className="h-4 w-4 text-muted-foreground mr-2" />
                          )}
                          <span className="text-muted-foreground">
                            {session.location} • {session.lastActiveAt ? new Date(session.lastActiveAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h2>
                <div className="space-y-3">
                  <div className="text-sm">
                    <p className="text-muted-foreground">Last login</p>
                    <p className="text-foreground">
                      {user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleString() : 'Never'}
                    </p>
                  </div>
                  <div className="text-sm">
                    <p className="text-muted-foreground">Password last changed</p>
                    <p className="text-foreground">
                      {userSecurity?.lastPasswordChange
                        ? new Date(userSecurity.lastPasswordChange).toLocaleDateString()
                        : 'Never'}
                    </p>
                  </div>
                  <div className="text-sm">
                    <p className="text-muted-foreground">Account created</p>
                    <p className="text-foreground">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-card rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-foreground">Remove User</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Are you sure you want to remove {user.name} from {currentTenant?.name}? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-accent text-foreground rounded-lg hover:bg-accent/80 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Remove User
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default UserDetail;